/**
 * Copyright (c) [2024] Jangouts
 *
 * This software may be modified and distributed under the terms
 * of the MIT license.  See the LICENSE.txt file for details.
 */

import { Janus } from '../vendor/janus';

// 信令类型枚举
export const CALL_SIGNALS = {
  INVITE: 'invite',       // 邀请通话
  ACCEPT: 'accept',       // 接受通话
  REJECT: 'reject',       // 拒绝通话
  RINGING: 'ringing',     // 正在响铃
  END: 'end',             // 结束通话
  BUSY: 'busy',           // 忙碌中
  CANCEL: 'cancel'        // 取消呼叫
};

/**
 * 创建一对一通话服务
 * @param {Object} config 配置对象
 * @param {Object} eventsService 事件服务
 * @param {Object} dataChannelService 数据通道服务
 * @returns {Object} 通话服务实例
 */
export const createCallService = (config, eventsService, dataChannelService) => {
  const { janusServer, janusDebug, janusWithCredentials } = config;
  
  let that = {
    janus: null,
    currentCall: null,
    callHandlers: new Map(), // 存储通话处理函数
    callTimeout: 30000 , // 呼叫超时时间（毫秒）30000
  };

  // 初始化Janus连接
  that.connect = () => {
    return new Promise((resolve, reject) => {
      if (that.janus) {
        resolve(true);
        return;
      }

      Janus.init({ debug: janusDebug });
      const jn = new Janus({
        server: janusServer,
        withCredentials: janusWithCredentials,
        success: () => {
          that.janus = jn;
          console.log('Janus connected for one-to-one calls');
          resolve(true);
        },
        error: (error) => {
          console.error('Janus connection error:', error);
          reject(error);
        }
      });
    });
  };

  // 创建临时通话房间
  that.createCallRoom = () => {
    return new Promise((resolve, reject) => {
      that.connect().then(() => {
        that.janus.attach({
          plugin: 'janus.plugin.videoroom',
          success: (pluginHandle) => {
            console.log('Creating call room...');
            // 生成随机的房间ID（10000-99999之间）
            const roomId = Math.floor(10000 + Math.random() * 90000);
            const request = {
              request: 'create',
              room: roomId,
              publishers: 2, // 最多2个发布者（一对一）
              bitrate: 128000, // 音频比特率
              //audiocodec: 'opus', // 音频编码
              //videocodec: 'vp8', // 视频编解码器，用户加入时通过 video: false 禁用视频发布
              permanent: true, // 永久房间
              record: true
            };

            pluginHandle.send({
              message: request,
              success: (result) => {
                console.log('Call room created========1:', result);
                if (result.videoroom === 'created') {
                  console.log(`Call room created: ${roomId}`);
                  pluginHandle.detach();
                  resolve({
                    roomId,
                    pluginHandleId: pluginHandle.getId()
                  });
                } else {
                  pluginHandle.detach();
                  reject(new Error('Failed to create room'));
                }
              },
              error: (error) => {
                pluginHandle.detach();
                reject(error);
              }
            });
          },
          error: (error) => {
            reject(error);
          }
        });
      })
        .catch(reject);
    });
  };

  // 发起呼叫
  that.initiateCall = async (remoteUserId, username, callId) => {
    try {
      // 创建通话房间
      const roomInfo = await that.createCallRoom();
      
      // 加入房间作为发布者（仅音频）
      const callInfo = {
        callId,
        roomId: roomInfo.roomId,
        remoteUserId,
        username,
        isInitiator: true,
        status: 'calling',
        startTime: Date.now()
      };

      that.currentCall = callInfo;

      // 通过DataChannel发送邀请信令
      that.sendCallSignal(CALL_SIGNALS.INVITE, {
        callId,
        roomId: roomInfo.roomId,
        callerId: username,
        timestamp: Date.now()
      });

      // 设置呼叫超时
      that.setCallTimeout(callId);

      return callInfo;
    } catch (error) {
      console.error('Failed to initiate call:', error);
      throw error;
    }
  };

  // 接受呼叫
  that.acceptCall = (callId, username) => {
    return new Promise((resolve, reject) => {
      if (!that.currentCall || that.currentCall.callId !== callId) {
        reject(new Error('Call not found'));
        return;
      }

      that.connect().then(() => {
        that.currentCall.status = 'accepted';
        
        // 发送接受信令
        that.sendCallSignal(CALL_SIGNALS.ACCEPT, {
          callId,
          timestamp: Date.now()
        });

        // 加入Janus房间
        that.joinCallRoom(that.currentCall.roomId, username, false)
          .then(resolve)
          .catch(reject);
      })
        .catch(reject);
    });
  };

  // 拒绝呼叫
  that.rejectCall = (callId, reason) => {
    if (!that.currentCall || that.currentCall.callId !== callId) {
      return Promise.reject(new Error('Call not found'));
    }

    that.sendCallSignal(CALL_SIGNALS.REJECT, {
      callId,
      reason,
      timestamp: Date.now()
    });

    that.cleanupCall(callId);
    return Promise.resolve();
  };

  // 结束通话
  that.endCall = (callId) => {
    if (!that.currentCall || that.currentCall.callId !== callId) {
      return Promise.reject(new Error('Call not found'));
    }

    that.sendCallSignal(CALL_SIGNALS.END, {
      callId,
      timestamp: Date.now()
    });

    that.cleanupCall(callId);
    return Promise.resolve();
  };

  // 加入通话房间
  that.joinCallRoom = (roomId, username, isPublisher = true) => {
    return new Promise((resolve, reject) => {
      that.connect().then(() => {
        that.janus.attach({
          plugin: 'janus.plugin.videoroom',
          success: (pluginHandle) => {
            console.log(`Joining call room ${roomId} as ${username}`);
            
            const request = {
              request: 'join',
              room: roomId,
              ptype: isPublisher ? 'publisher' : 'subscriber',
              display: username,
              audio: true,
              video: false // 禁用视频
            };

            pluginHandle.send({
              message: request,
              success: (result) => {
                if (result.videoroom === 'joined') {
                  console.log(`Successfully joined room ${roomId}`);
                  
                  // 建立WebRTC连接
                  that.setupWebRTC(pluginHandle, result, isPublisher)
                    .then(() => {
                      resolve({
                        pluginHandle,
                        privateId: result.private_id,
                        roomId
                      });
                    })
                    .catch(reject);
                } else {
                  pluginHandle.detach();
                  reject(new Error('Failed to join room'));
                }
              },
              error: (error) => {
                pluginHandle.detach();
                reject(error);
              }
            });
          },
          error: (error) => {
            reject(error);
          }
        });
      })
        .catch(reject);
    });
  };

  // 设置WebRTC连接
  that.setupWebRTC = (pluginHandle, joinResult, isPublisher) => {
    return new Promise((resolve, reject) => {
      if (isPublisher) {
        // 发布者创建offer
        pluginHandle.createOffer({
          media: {
            audioSend: true,
            videoSend: false,
            audioRecv: true,
            videoRecv: false,
            data: true
          },
          success: (jsep) => {
            console.log('Publisher offer created');
            const publishRequest = {
              request: 'configure',
              audio: true,
              video: false,
              data: true
            };

            pluginHandle.send({
              message: publishRequest,
              jsep: jsep,
              success: (result) => {
                console.log('Publisher configured successfully');
                resolve();
              },
              error: (error) => {
                console.error('Publisher configuration error:', error);
                reject(error);
              }
            });
          },
          error: (error) => {
            console.error('Create offer error:', error);
            reject(error);
          }
        });
      } else {
        // 订阅者处理现有的发布者
        if (joinResult.publishers && joinResult.publishers.length > 0) {
          const publisher = joinResult.publishers[0];
          const subscribeRequest = {
            request: 'join',
            room: joinResult.room,
            ptype: 'subscriber',
            feed: publisher.id
          };

          pluginHandle.send({
            message: subscribeRequest,
            success: (result) => {
              if (result.videoroom === 'attached') {
                console.log('Subscriber attached to feed');
                resolve();
              } else {
                reject(new Error('Failed to attach subscriber'));
              }
            },
            error: (error) => {
              reject(error);
            }
          });
        } else {
          reject(new Error('No publisher found in room'));
        }
      }
    });
  };

  // 发送通话信令
  that.sendCallSignal = (signal, data) => {
    const message = {
      type: 'callSignal',
      content: {
        signal,
        ...data,
        timestamp: Date.now()
      }
    };

    // 通过DataChannel发送信令
    console.log('Sending call signal:', message.content);
    dataChannelService.sendMessage('callSignal', message.content);
    
    // 触发本地事件
    eventsService.roomEvent('callSignal', message.content);
  };

  // 处理接收到的信令
  that.handleCallSignal = (signalData) => {
    const { signal, callId, ...data } = signalData;
    
    console.log(`Received call signal: ${signal} for call ${callId}`);
    
    switch (signal) {
      case CALL_SIGNALS.INVITE:
        that.handleCallInvite(callId, data);
        break;
      case CALL_SIGNALS.ACCEPT:
        that.handleCallAccept(callId, data);
        break;
      case CALL_SIGNALS.REJECT:
        that.handleCallReject(callId, data);
        break;
      case CALL_SIGNALS.RINGING:
        that.handleCallRinging(callId, data);
        break;
      case CALL_SIGNALS.END:
        that.handleCallEnd(callId, data);
        break;
      case CALL_SIGNALS.BUSY:
        that.handleCallBusy(callId, data);
        break;
      case CALL_SIGNALS.CANCEL:
        that.handleCallCancel(callId, data);
        break;
      default:
        console.warn(`Unknown call signal: ${signal}`);
    }
  };

  // 处理呼叫邀请
  that.handleCallInvite = (callId, data) => {
    that.currentCall = {
      callId,
      roomId: data.roomId,
      remoteUserId: data.callerId,
      isInitiator: false,
      status: 'incoming',
      startTime: Date.now()
    };

    // 触发来电事件
    eventsService.roomEvent('incomingCall', {
      callId,
      callerId: data.callerId,
      roomId: data.roomId,
      timestamp: data.timestamp
    });
  };

  // 处理呼叫接受
  that.handleCallAccept = (callId, data) => {
    if (that.currentCall && that.currentCall.callId === callId) {
      that.currentCall.status = 'accepted';
      
      // 清除呼叫超时
      that.clearCallTimeout(callId);
      
      eventsService.roomEvent('callAccepted', {
        callId,
        timestamp: data.timestamp
      });
    }
  };

  // 处理呼叫拒绝
  that.handleCallReject = (callId, data) => {
    if (that.currentCall && that.currentCall.callId === callId) {
      eventsService.roomEvent('callRejected', {
        callId,
        reason: data.reason,
        timestamp: data.timestamp
      });
      
      that.cleanupCall(callId);
    }
  };

  // 处理响铃
  that.handleCallRinging = (callId, data) => {
    if (that.currentCall && that.currentCall.callId === callId) {
      eventsService.roomEvent('callRinging', {
        callId,
        timestamp: data.timestamp
      });
    }
  };

  // 处理通话结束
  that.handleCallEnd = (callId, data) => {
    eventsService.roomEvent('callEnded', {
      callId,
      timestamp: data.timestamp
    });
    
    that.cleanupCall(callId);
  };

  // 处理忙碌状态
  that.handleCallBusy = (callId, data) => {
    eventsService.roomEvent('callBusy', {
      callId,
      timestamp: data.timestamp
    });
    
    that.cleanupCall(callId);
  };

  // 处理呼叫取消
  that.handleCallCancel = (callId, data) => {
    eventsService.roomEvent('callCancelled', {
      callId,
      timestamp: data.timestamp
    });
    
    that.cleanupCall(callId);
  };

  // 设置呼叫超时
  that.setCallTimeout = (callId) => {
    that.clearCallTimeout(callId);
    
    const timeoutId = setTimeout(() => {
      if (that.currentCall && that.currentCall.callId === callId) {
        console.log(`Call ${callId} timed out`);
        eventsService.roomEvent('callTimeout', { callId });
        that.cleanupCall(callId);
      }
    }, that.callTimeout);

    that.callHandlers.set(`${callId}_timeout`, timeoutId);
  };

  // 清除呼叫超时
  that.clearCallTimeout = (callId) => {
    const timeoutId = that.callHandlers.get(`${callId}_timeout`);
    if (timeoutId) {
      clearTimeout(timeoutId);
      that.callHandlers.delete(`${callId}_timeout`);
    }
  };

  // 清理通话资源
  that.cleanupCall = (callId) => {
    // 清除所有相关的定时器
    that.clearCallTimeout(callId);
    
    // 重置当前通话状态
    if (that.currentCall && that.currentCall.callId === callId) {
      that.currentCall = null;
    }
    
    // 触发清理完成事件
    eventsService.roomEvent('callCleaned', { callId });
  };

  // 获取当前通话状态
  that.getCurrentCall = () => {
    return that.currentCall;
  };

  // 检查是否在通话中
  that.isInCall = () => {
    return that.currentCall !== null && that.currentCall.status === 'accepted';
  };

  return that;
};